import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  TeamOutlined,
  UserOutlined,
  SolutionOutlined,
  NotificationOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useBatches, batchLabel } from '../hooks/useBatches';
import { useImChannel } from '../hooks/useImChannel';
import {
  adminPageWrap,
  adminAlertStyle,
  adminFilterCardStyle,
  adminContentCardStyle,
} from '../styles/adminPageShell';

/**
 * 「表彰提名指定」管理员页面（2026-05-12 新增，与「提名领导指定」是孪生页）。
 *
 * 业务流程：
 *   HR 在此页录入「部门 + 表彰提名领导 + 可提名上限」→ 一键钉钉推送给该领导 →
 *   领导登录后进入「表彰提名」页（/recognition-nominator）按上限填写要表彰的员工。
 *   汇总结果在「职级晋升人员」页末尾的「表彰人员」卡片里只读展示。
 *
 * 与「提名领导指定」的差异：
 *   - 多了一个"推送"按钮：本模块需要主动钉钉通知（业务来由：老板要求"评价人在
 *     钉钉上打开人才盘点系统，就可以看到表彰提名板块"）。
 *   - quota 直接存 RecognitionLeaderAssignment.quota（不再走 batch.json）。
 *   - 同一部门可指派多名领导（无任何约束），与晋升提名一致。
 */

type RecognitionAssignment = {
  id: number;
  batchId: number;
  deptId: number | null;
  deptName: string;
  leaderId: number | null;
  leaderName: string;
  quota: number | null;
  notifiedAt: string | null;
  effectiveAt: string;
  revokedAt: string | null;
  /** 后端在 listAssignments 里 join 计算出来的"该领导本期已提交表彰数"。 */
  used?: number;
};

type NotifyResult = { sent?: boolean; reason?: string };

const { Text } = Typography;

export function RecognitionLeadersPage() {
  const qc = useQueryClient();
  const { batches, batchId, setBatchId } = useBatches();
  const { channel, label: imLabel } = useImChannel();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const { data: assignments = [] } = useQuery<RecognitionAssignment[]>({
    queryKey: ['recognition-leaders', batchId],
    queryFn: () =>
      api.get('/admin/recognition-leaders', { params: { batchId } }),
    enabled: !!batchId,
  });

  const stats = useMemo(() => {
    const leaderIds = new Set(
      assignments.filter((a) => a.leaderId != null).map((a) => a.leaderId),
    );
    const leaderNames = new Set(
      assignments.map((a) => (a.leaderName ?? '').trim()).filter(Boolean),
    );
    const totalQuota = assignments.reduce((s, a) => s + (a.quota ?? 0), 0);
    const totalUsed = assignments.reduce((s, a) => s + (a.used ?? 0), 0);
    return {
      assignmentCount: assignments.length,
      leaderCount: leaderIds.size || leaderNames.size,
      totalQuota,
      totalUsed,
    };
  }, [assignments]);

  const notifyMutation = useMutation({
    mutationFn: (id: number) =>
      api.post<any, NotifyResult>(`/admin/recognition-leaders/${id}/notify`),
    onSuccess: (data: NotifyResult) => {
      if (data?.sent === false) {
        message.info(data.reason ?? '企微通知暂未开通，未发送通知');
      } else {
        message.success(`已发送${imLabel}通知`);
      }
      qc.invalidateQueries({ queryKey: ['recognition-leaders'] });
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message ?? err?.message ?? '推送失败'),
  });

  /**
   * 「新增 = 自动推送」业务约定（农俊董事长 2026-05-12 提）：
   *   - 创建指派成功后，立刻调 notify 接口给该领导发钉钉，HR 不需要再单独点"推送"。
   *   - 编辑（PATCH）不自动推送：通常只是改 quota / 部门，不应骚扰领导。
   *   - 推送失败 ≠ 指派失败：保存动作已成功，仅提示推送失败原因，列表 UI 不阻塞。
   */
  const submitMutation = useMutation({
    mutationFn: (vals: {
      deptName: string;
      leaderName: string;
      quota?: number;
    }) =>
      editingId
        ? api.patch(`/admin/recognition-leaders/${editingId}`, vals)
        : api.post('/admin/recognition-leaders', { batchId, ...vals }),
    onSuccess: async (data: any) => {
      const wasCreate = !editingId;
      message.success(
        wasCreate
          ? channel === 'wecom'
            ? '已指定，正在确认企微通知状态…'
            : `已指定，正在推送${imLabel}…`
          : '已更新',
      );
      setModalOpen(false);
      setEditingId(null);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['recognition-leaders'] });

      const newId: number | undefined = (data as RecognitionAssignment | null)
        ?.id;
      if (wasCreate && newId) {
        try {
          const notifyResult = await api.post<any, NotifyResult>(
            `/admin/recognition-leaders/${newId}/notify`,
          );
          if (notifyResult?.sent === false) {
            message.info(notifyResult.reason ?? '企微通知暂未开通，已保存但未发送通知');
          } else {
            message.success(`已发送${imLabel}通知`);
          }
        } catch (err: any) {
          message.warning(
            `指派已保存，但${imLabel}推送失败：${
              err?.response?.data?.message ?? err?.message ?? '未知原因'
            }。可在列表中点"再推送"重试。`,
            6,
          );
        }
        qc.invalidateQueries({ queryKey: ['recognition-leaders'] });
      }
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message ?? err?.message ?? '保存失败'),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/recognition-leaders/${id}`),
    onSuccess: () => {
      message.success('已撤销');
      qc.invalidateQueries({ queryKey: ['recognition-leaders'] });
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message ?? err?.message ?? '撤销失败'),
  });

  /**
   * 顶部"一键全部推送"：把本批次所有未推送的指派全部跑一遍 notify。
   * 设计取舍：顺序串行（领导数 ≤ 20，不会卡）而非并发，便于错误追溯；已推送过的直接重推（HR 主动重推也算）。
   */
  const [bulkNotifying, setBulkNotifying] = useState(false);
  const handleBulkNotify = async () => {
    if (assignments.length === 0) return;
    setBulkNotifying(true);
    const okList: string[] = [];
    const failList: Array<{ name: string; reason: string }> = [];
    for (const row of assignments) {
      try {
        const result = await api.post<any, NotifyResult>(
          `/admin/recognition-leaders/${row.id}/notify`,
        );
        if (result?.sent === false) {
          failList.push({
            name: row.leaderName,
            reason: result.reason ?? '企微通知暂未开通，未发送通知',
          });
        } else {
          okList.push(row.leaderName);
        }
      } catch (err: any) {
        failList.push({
          name: row.leaderName,
          reason:
            err?.response?.data?.message ?? err?.message ?? '未知原因',
        });
      }
    }
    setBulkNotifying(false);
    qc.invalidateQueries({ queryKey: ['recognition-leaders'] });

    if (
      okList.length === 0 &&
      failList.length > 0 &&
      failList.every((item) => item.reason.includes('企微通知暂未开通'))
    ) {
      message.info(`企微通知暂未开通，${failList.length} 条指派已保存但未发送通知`);
      return;
    }

    if (failList.length === 0) {
      message.success(`已全部推送（${okList.length} 人）`);
    } else {
      Modal.warning({
        title: `推送完成：成功 ${okList.length}，失败 ${failList.length}`,
        width: 520,
        content: (
          <div>
            {okList.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <strong>成功：</strong>
                {okList.join('、')}
              </div>
            )}
            <div>
              <strong style={{ color: '#cf1322' }}>失败：</strong>
              <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                {failList.map((f, idx) => (
                  <li key={idx}>
                    {f.name}：{f.reason}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ),
      });
    }
  };

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (row: RecognitionAssignment) => {
    setEditingId(row.id);
    form.setFieldsValue({
      deptName: row.deptName ?? '',
      leaderName: row.leaderName ?? '',
      quota: row.quota ?? undefined,
    });
    setModalOpen(true);
  };

  const formatTime = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('zh-CN', { hour12: false });
    } catch {
      return iso;
    }
  };

  return (
    <div style={adminPageWrap}>
      <div className="tg-page-title">表彰提名指定</div>

      <Alert
        type="info"
        showIcon
        style={adminAlertStyle}
        message={`维护「表彰提名领导 + 部门 + 上限」名单，新增即自动推送${imLabel}`}
        description={
          <Text type="secondary">
            新增一条指派后系统会立刻给该领导发{imLabel}通知（无需再点"推送"）。需要重发可点行内"再推送"，或顶部"一键全部推送"。
            汇总结果在管理员的「职级晋升人员」页末尾的「表彰人员」卡片里只读展示，**不影响**该页上的任何现有数据。
          </Text>
        }
      />

      <Card
        style={{ ...adminFilterCardStyle, marginBottom: 16 }}
        styles={{ body: { paddingBottom: 12 } }}
      >
        <Space wrap size={12} style={{ marginBottom: 12 }}>
          <Select
            placeholder="选择批次"
            style={{ width: 220 }}
            value={batchId}
            options={batches.map((b) => ({
              label: batchLabel(b),
              value: b.id,
            }))}
            onChange={setBatchId}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!batchId}
            onClick={openCreate}
          >
            新增指派
          </Button>
          <Button
            icon={<NotificationOutlined />}
            disabled={!batchId || assignments.length === 0}
            loading={bulkNotifying}
            onClick={handleBulkNotify}
          >
            一键全部推送（{assignments.length}）
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            新增即自动推{imLabel}；列表的"再推送"用于重发；撤销 = 软删除（已提交的表彰仍保留）。
          </Text>
        </Space>

        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="本批次指派条数"
              value={stats.assignmentCount}
              prefix={<SolutionOutlined style={{ color: '#722ed1' }} />}
              suffix="行"
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="覆盖领导人数"
              value={stats.leaderCount}
              prefix={<UserOutlined style={{ color: '#1677ff' }} />}
              suffix="人"
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="上限合计"
              value={stats.totalQuota}
              prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
              suffix={stats.totalQuota > 0 ? '人' : ''}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="已提名"
              value={stats.totalUsed}
              prefix={<NotificationOutlined style={{ color: '#fa8c16' }} />}
              suffix="人"
            />
          </Col>
        </Row>
      </Card>

      <Card
        style={adminContentCardStyle}
        styles={{ body: { paddingTop: 12, paddingBottom: 12 } }}
      >
        <Table<RecognitionAssignment>
          rowKey="id"
          dataSource={assignments}
          size="middle"
          pagination={{ pageSize: 20, showTotal: (n) => `共 ${n} 行` }}
          columns={[
            {
              title: '提名领导',
              key: 'leader',
              render: (_, r) => (
                <Space size={6}>
                  <span style={{ fontWeight: 500 }}>{r.leaderName ?? '-'}</span>
                  {r.leaderId == null && r.leaderName ? (
                    <Tag color="warning">未匹配花名册</Tag>
                  ) : null}
                </Space>
              ),
            },
            {
              title: '提名部门',
              key: 'dept',
              render: (_, r) =>
                r.deptName ? (
                  <span>{r.deptName}</span>
                ) : (
                  <Text type="secondary">—</Text>
                ),
            },
            {
              title: '上限',
              key: 'quota',
              width: 100,
              align: 'center',
              render: (_, r) =>
                r.quota == null ? (
                  <Text type="secondary">不限</Text>
                ) : (
                  <span style={{ fontWeight: 600, color: '#1677ff' }}>
                    {r.quota}
                  </span>
                ),
            },
            {
              title: '已提名',
              key: 'used',
              width: 80,
              align: 'center',
              render: (_, r) =>
                r.used == null ? (
                  <Text type="secondary">—</Text>
                ) : (
                  <span>{r.used}</span>
                ),
            },
            {
              title: '上次推送',
              key: 'notify',
              width: 170,
              render: (_, r) =>
                r.notifiedAt ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatTime(r.notifiedAt)}
                  </Text>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
                ),
            },
            {
              title: '操作',
              key: 'op',
              width: 240,
              align: 'center',
              render: (_, r) => (
                <Space size={4}>
                  <Popconfirm
                    title={`再次推送${imLabel}？`}
                    description={`将给该领导重新发一条${imLabel}消息，提示其进入「表彰提名」入口`}
                    onConfirm={() => notifyMutation.mutate(r.id)}
                  >
                    <Button
                      type="link"
                      size="small"
                      icon={<NotificationOutlined />}
                    >
                      再推送
                    </Button>
                  </Popconfirm>
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openEdit(r)}
                  >
                    编辑
                  </Button>
                  <Popconfirm
                    title="撤销该指派？"
                    description="撤销后该领导失去本期表彰提名权（已提交的表彰仍保留）"
                    onConfirm={() => revokeMutation.mutate(r.id)}
                  >
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                    >
                      撤销
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editingId ? '编辑表彰指派' : '新增表彰指派'}
        open={modalOpen}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalOpen(false);
          setEditingId(null);
          form.resetFields();
        }}
        confirmLoading={submitMutation.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) =>
            submitMutation.mutate({
              deptName: (v.deptName ?? '').trim(),
              leaderName: (v.leaderName ?? '').trim(),
              quota: v.quota,
            })
          }
        >
          <Form.Item
            label="表彰提名领导"
            name="leaderName"
            rules={[{ required: true, message: '请填写领导姓名' }]}
            extra={`按花名册姓名精确匹配；未命中也可保存，但推送${imLabel}前需先在花名册更正姓名一致。`}
          >
            <Input placeholder="例如：邓金婷" />
          </Form.Item>
          <Form.Item
            label="提名部门"
            name="deptName"
            rules={[{ required: true, message: '请填写部门名称' }]}
            extra={`该领导主要负责的部门（自由文本，仅作${imLabel}文案与汇总展示用）。同一部门可指派多名领导。`}
          >
            <Input placeholder="例如：瑞小美-南宁万象城大师店" />
          </Form.Item>
          <Form.Item
            label="可提名上限"
            name="quota"
            extra="该领导本批次可提交的最大表彰人数；留空 = 不限"
          >
            <InputNumber
              min={0}
              max={50}
              step={1}
              precision={0}
              controls={false}
              style={{ width: '100%' }}
              placeholder="例如：3"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
